import { Suspense, lazy, useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { TAB_TYPES } from '@/context/TabsContext';

// Lazy load page components for better performance
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const BookingManagement = lazy(() => import('@/pages/BookingManagement'));
const GuestManagement = lazy(() => import('@/pages/GuestManagement'));
const RoomManagement = lazy(() => import('@/pages/RoomManagement'));
const RoomForm = lazy(() => import('@/pages/RoomForm'));
const ServiceManagement = lazy(() => import('@/pages/ServiceManagement'));
const ServiceOrders = lazy(() => import('@/pages/ServiceOrders'));
const RestaurantManagement = lazy(() => import('@/pages/RestaurantManagement'));
const RestaurantMenu = lazy(() => import('@/pages/MenuManagement'));
const MenuCategoryManagement = lazy(() => import('@/pages/MenuCategoryManagement'));
const RestaurantTables = lazy(() => import('@/pages/RestaurantTables'));
const OrderHistory = lazy(() => import('@/pages/OrderHistory'));
const CreditAccounts = lazy(() => import('@/pages/CreditAccounts'));
const SupportTickets = lazy(() => import('@/pages/SupportTickets'));
const UserManagement = lazy(() => import('@/pages/UserManagement'));
const IdTypeManagement = lazy(() => import('@/pages/IDTypeManagement'));
const BudgetPlanning = lazy(() => import('@/pages/BudgetDashboard'));
const BudgetSetup = lazy(() => import('@/pages/BudgetSetup'));
const BudgetAllocations = lazy(() => import('@/pages/BudgetAllocations'));
const FinanceSetup = lazy(() => import('@/pages/FinanceSetup'));
const CompanyManagement = lazy(() => import('@/pages/CompanyManagement'));
const SuperAdminDashboard = lazy(() => import('@/pages/SuperAdminDashboard'));
const SuperAdminUserManagement = lazy(() => import('@/pages/SuperAdminUserManagement'));
const CompanyUserManagement = lazy(() => import('@/pages/CompanyUsers'));
const RoleManagement = lazy(() => import('@/pages/RoleManagement'));
const ThemeSettings = lazy(() => import('@/pages/ThemeSettings'));
const PaymentSettings = lazy(() => import('@/pages/PaymentSettings'));
const CommunicationSettings = lazy(() => import('@/pages/CommunicationSettings'));
const POSSettings = lazy(() => import('@/pages/POSSettings'));
const KDSSettings = lazy(() => import('@/pages/KDSSettings'));
const LockSettings = lazy(() => import('@/pages/LockSettings'));
const PhoneSettings = lazy(() => import('@/pages/PhoneSettings'));
const PBXManagement = lazy(() => import('@/pages/PBXManagement'));
const AuditTrail = lazy(() => import('@/pages/AuditTrail'));
const LiveSessions = lazy(() => import('@/pages/LiveSessions'));
const FinanceDashboard = lazy(() => import('@/pages/FinanceDashboard'));
const PendingPostings = lazy(() => import('@/pages/PendingPostings'));
const LedgerManagement = lazy(() => import('@/pages/LedgerManagement'));
const JournalEntries = lazy(() => import('@/pages/JournalEntryPage'));
const FinancialReports = lazy(() => import('@/pages/FinancialReports'));
const GSTReports = lazy(() => import('@/pages/GSTReports'));
const ReportsDashboard = lazy(() => import('@/pages/ReportsPage'));
// Assets
const AssetManagement = lazy(() => import('@/pages/AssetManagement'));
// Purchase
const VendorManagement = lazy(() => import('@/pages/VendorsPage'));
const PurchaseRequisitions = lazy(() => import('@/pages/PurchaseRequisitionsPage'));
const PurchaseOrders = lazy(() => import('@/pages/PurchaseOrdersPage'));
const GoodsReceiptNote = lazy(() => import('@/pages/GRNPage'));
const PurchaseInvoices = lazy(() => import('@/pages/PurchaseInvoicesPage'));
// Inventory
const StoreManagement = lazy(() => import('@/pages/StoresPage'));
const ItemMaster = lazy(() => import('@/pages/ItemMasterPage'));
const StockMovements = lazy(() => import('@/pages/StockMovementsPage'));
// Online Portal
const PortalSettings = lazy(() => import('@/pages/PortalSettings'));
const PropertyListing = lazy(() => import('@/pages/PropertyListing'));
const PortalUsers = lazy(() => import('@/pages/PortalUsers'));
const PropertyRegistrations = lazy(() => import('@/pages/PropertyRegistrations'));
const PropertyConfigurePage = lazy(() => import('@/pages/PropertyConfigurePage'));
// Super Admin
const CreateHotel = lazy(() => import('@/pages/CreateHotel'));
const HotelDetails = lazy(() => import('@/pages/HotelDetails'));
// Cloud Storage
const CloudflareR2Settings = lazy(() => import('@/pages/CloudflareR2Settings'));

// Map TAB_TYPES to their respective page components
const PAGE_COMPONENTS = {
  [TAB_TYPES.PAGE_DASHBOARD]: Dashboard,
  [TAB_TYPES.PAGE_BOOKINGS]: BookingManagement,
  [TAB_TYPES.PAGE_GUESTS]: GuestManagement,
  [TAB_TYPES.PAGE_ROOMS]: RoomManagement,
  [TAB_TYPES.ROOM_ADD]: RoomForm,
  [TAB_TYPES.ROOM_EDIT]: RoomForm,
  [TAB_TYPES.PAGE_SERVICES]: ServiceManagement,
  [TAB_TYPES.PAGE_SERVICE_ORDERS]: ServiceOrders,
  [TAB_TYPES.PAGE_RESTAURANTS]: RestaurantManagement,
  [TAB_TYPES.PAGE_RESTAURANT_MENU]: RestaurantMenu,
  [TAB_TYPES.PAGE_MENU_CATEGORIES]: MenuCategoryManagement,
  [TAB_TYPES.PAGE_RESTAURANT_TABLES]: RestaurantTables,
  [TAB_TYPES.PAGE_ORDER_HISTORY]: OrderHistory,
  [TAB_TYPES.PAGE_CREDIT_ACCOUNTS]: CreditAccounts,
  [TAB_TYPES.PAGE_SUPPORT_TICKETS]: SupportTickets,
  [TAB_TYPES.PAGE_USERS]: UserManagement,
  [TAB_TYPES.PAGE_ID_TYPES]: IdTypeManagement,
  [TAB_TYPES.PAGE_BUDGET]: BudgetPlanning,
  [TAB_TYPES.PAGE_BUDGET_SETUP]: BudgetSetup,
  [TAB_TYPES.PAGE_BUDGET_ALLOCATIONS]: BudgetAllocations,
  [TAB_TYPES.PAGE_FINANCE_SETUP]: FinanceSetup,
  [TAB_TYPES.PAGE_COMPANIES]: CompanyManagement,
  [TAB_TYPES.PAGE_HOTELS]: SuperAdminDashboard,
  [TAB_TYPES.PAGE_SUPER_ADMIN_USERS]: SuperAdminUserManagement,
  [TAB_TYPES.PAGE_COMPANY_USERS]: CompanyUserManagement,
  [TAB_TYPES.PAGE_ROLES]: RoleManagement,
  [TAB_TYPES.PAGE_THEME_SETTINGS]: ThemeSettings,
  [TAB_TYPES.PAGE_PAYMENT_SETTINGS]: PaymentSettings,
  [TAB_TYPES.PAGE_COMMUNICATION_SETTINGS]: CommunicationSettings,
  [TAB_TYPES.PAGE_POS_SETTINGS]: POSSettings,
  [TAB_TYPES.PAGE_KDS_SETTINGS]: KDSSettings,
  [TAB_TYPES.PAGE_LOCK_SETTINGS]: LockSettings,
  [TAB_TYPES.PAGE_PHONE_SETTINGS]: PhoneSettings,
  [TAB_TYPES.PAGE_FINANCE_DASHBOARD]: FinanceDashboard,
  [TAB_TYPES.PAGE_PENDING_POSTINGS]: PendingPostings,
  [TAB_TYPES.PAGE_LEDGERS]: LedgerManagement,
  [TAB_TYPES.PAGE_JOURNAL_ENTRIES]: JournalEntries,
  [TAB_TYPES.PAGE_FINANCIAL_REPORTS]: FinancialReports,
  [TAB_TYPES.PAGE_GST_REPORTS]: GSTReports,
  [TAB_TYPES.PAGE_REPORTS]: ReportsDashboard,
  // Assets
  [TAB_TYPES.PAGE_ASSETS]: AssetManagement,
  // Purchase
  [TAB_TYPES.PAGE_VENDORS]: VendorManagement,
  [TAB_TYPES.PAGE_REQUISITIONS]: PurchaseRequisitions,
  [TAB_TYPES.PAGE_PURCHASE_ORDERS]: PurchaseOrders,
  [TAB_TYPES.PAGE_GRN]: GoodsReceiptNote,
  [TAB_TYPES.PAGE_PURCHASE_INVOICES]: PurchaseInvoices,
  // Inventory
  [TAB_TYPES.PAGE_STORES]: StoreManagement,
  [TAB_TYPES.PAGE_ITEM_MASTER]: ItemMaster,
  [TAB_TYPES.PAGE_STOCK_MOVEMENTS]: StockMovements,
  // Online Portal
  [TAB_TYPES.PAGE_PORTAL_SETTINGS]: PortalSettings,
  [TAB_TYPES.PAGE_PROPERTY_REGISTRATIONS]: PropertyRegistrations,
  [TAB_TYPES.PAGE_PROPERTY_LISTING]: PropertyListing,
  [TAB_TYPES.PAGE_PORTAL_USERS]: PortalUsers,
  [TAB_TYPES.PAGE_PROPERTY_CONFIGURE]: PropertyConfigurePage,
  // Super Admin
  [TAB_TYPES.PAGE_CREATE_HOTEL]: CreateHotel,
  [TAB_TYPES.PAGE_HOTEL_DETAILS]: HotelDetails,
  [TAB_TYPES.PAGE_PBX_MANAGEMENT]: PBXManagement,
  [TAB_TYPES.PAGE_AUDIT_TRAIL]: AuditTrail,
  [TAB_TYPES.PAGE_LIVE_SESSIONS]: LiveSessions,
  // Cloud Storage
  [TAB_TYPES.PAGE_CLOUD_STORAGE]: CloudflareR2Settings,
};

const LoadingFallback = () => (
  <div className="flex items-center justify-center h-full">
    <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
    <span className="ml-2 text-gray-600">Loading...</span>
  </div>
);

export default function PageTab({ tabId, data, tabType }) {
  const PageComponent = useMemo(() => PAGE_COMPONENTS[tabType], [tabType]);

  if (!PageComponent) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <p>Unknown page type: {tabType}</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-gray-50">
      <Suspense fallback={<LoadingFallback />}>
        <PageComponent isTab={true} tabId={tabId} tabData={data} />
      </Suspense>
    </div>
  );
}
